import { z } from 'zod/v4'

import { jsonDocument } from '../../contract/index.js'
import { ADMIN_JOB_STATUSES } from '../../contracts/admin/admin-jobs.contracts.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { pageFields } from './page-fields.js'
import { adminPaginationSchema } from './users.screen.js'

/*
 * «Trabajos» of the market data panel as a screen (`admin.marketData.jobs`): one page of jobs and the figures of its
 * cards. The row is the one `toMarketDataJobRow` builds (#4513): an empty optional is ABSENT, never `null`, and
 * `status` is text as in `MarketDataJobResponse`. The live channel (`/api/sse/market-data-jobs`) is apart (#4093).
 */

/** One free document of a job row: each processor writes its own keys, which no schema of the panel names. */
const jobDocument = (source: string) =>
  jsonDocument({
    reason: `${source}: written by each job processor with its own keys, shown as received`,
  })

export const marketDataJobRowSchema = z.strictObject({
  id: z.string(),
  jobType: z.string(),
  status: z.string(),
  progress: z.number(),
  progressMessage: z.string().optional(),
  inputData: jobDocument('platform_jobs.input_data'),
  outputData: jobDocument('platform_jobs.output_data').optional(),
  errorData: jobDocument('platform_jobs.error_data').optional(),
  metadata: jobDocument('platform_jobs.metadata').optional(),
  createdAt: z.string(),
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
  processingTimeMs: z.number().optional(),
  currentRetry: z.number(),
  maxRetries: z.number(),
  checkpoint: z
    .strictObject({
      stage: z.string(),
      // The import cursors are `{ type, value }` (each processor may add its own keys); an absent one travels as
      // `null` (`toMarketDataJobRow`).
      cursor: jobDocument('job_checkpoints.cursor').nullable(),
      lastUpdated: z.string().nullable(),
    })
    .nullable()
    .optional(),
})

export const marketDataJobStatsSchema = z.strictObject({
  /** Pending and processing: the jobs on the move. A `paused` job is in no figure. */
  active: z.number(),
  completed: z.number(),
  failed: z.number(),
  cancelled: z.number(),
  retryableFailed: z.number(),
  permanentFailed: z.number(),
})

/** The columns the page may order by: the one list the query, the service allowlist and the assertions share. */
export const MARKET_DATA_JOB_SORT_KEYS = ['created_at', 'status', 'job_type', 'progress'] as const

/**
 * The names of `GET …/market-data/jobs` without `exchange`, which the route ignored; `limit` goes up to 100, what the
 * table offers.
 */

export const adminMarketDataJobsQuerySchema = z.strictObject({
  // The six stored statuses (`paused` included: a paused job can be asked for). `stats.active` still counts only the
  // jobs on the move (pending and processing): a paused one is in no card, but it is on the page when asked for.
  status: z.enum(ADMIN_JOB_STATUSES).optional(),
  action: z.enum(['download', 'fill-gaps', 'export', 'import']).optional(),
  search: z.string().trim().min(1).max(100).optional(),
  jobType: z.string().trim().min(1).max(50).optional(),
  sortBy: z.enum(MARKET_DATA_JOB_SORT_KEYS).default('created_at'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  ...pageFields(25),
})

export const adminMarketDataJobsScreen = defineScreen({
  id: 'admin.marketData.jobs',
  path: '/api/screens/admin/market-data/jobs',
  access: 'super_admin',
  query: adminMarketDataJobsQuerySchema,
  response: z.strictObject({
    page: screenSection(
      z.strictObject({
        items: z.array(marketDataJobRowSchema),
        pagination: adminPaginationSchema,
      }),
    ),
    stats: screenSection(marketDataJobStatsSchema),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.marketData'],
  // statements: 2 (count + page) + one checkpoint read per row (25 by default, 100 at most: 104) + 2 (the figures).
  budget: { requests: 1, statements: 29, bytes: 80_000, p95Ms: 600 },
  summary: 'Market data jobs: one page of jobs and their figures',
})

export type AdminMarketDataJobsQuery = ScreenQuery<typeof adminMarketDataJobsScreen>
export type AdminMarketDataJobsData = ScreenData<typeof adminMarketDataJobsScreen>
